import '../styles.scss';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import Card from '../../../../common/card';
import OnboardingDialog from '../../../../common/onboarding-dialog';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../../../../config/routes';
import l from '../../../../../lang';
import { usePayouts } from '../../../../../store/payouts';
import { useHome } from '../../../../../store/home';
import { PayoutCreationStep } from '../../../../../store/payouts/interfaces';
import {
  getAccountTypeDisplayLabel,
  getBankDisplayName,
  getFieldLabel,
  getFlowTypeLabel,
  getPayoutWizardMenuItems,
  mapCreatePayoutDataToFieldPathValues,
  payoutPurposeOptions,
} from '../utils';
import OTPModal from '../otp-modal';

const formatLabel = (fieldPath: string) => {
  const sanitized = fieldPath.replace('beneficiary.', '');
  return sanitized.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
};

const Review = () => {
  const history = useHistory();
  const [showOTPModal, setShowOTPModal] = useState(false);
  const { createPayout, setCreatePayoutStep, resetCreatePayout, getBanks } = usePayouts();
  const { balances, getBalances } = useHome();

  useEffect(() => {
    if (createPayout.step < PayoutCreationStep.PAYOUT_DATA) {
      history.push(Routes.PAYOUTS_STEP_ACCOUNT_DATA);
      return;
    }
    setCreatePayoutStep(PayoutCreationStep.REVIEW);
    getBalances();

    const transferCountry = createPayout.createPayoutData.transferCountry;
    if (transferCountry) {
      getBanks(transferCountry);
    }
  }, []);

  const sortedBankAccountEntries = useMemo(() => {
    const ignoredFields = new Set(['amount', 'currency_to_pay', 'purpose', 'description', 'country', 'flow_type']);
    const fieldPathValues = mapCreatePayoutDataToFieldPathValues(createPayout.createPayoutData);
    return Object.entries(fieldPathValues)
      .filter(([key]) => !ignoredFields.has(key))
      .sort((entryA, entryB) => entryA[0].localeCompare(entryB[0]));
  }, [createPayout.createPayoutData, createPayout.bankOptions]);

  const handleSuccess = () => {
    setShowOTPModal(false);
    resetCreatePayout();
    history.push(Routes.PAYOUTS);
  };

  return (
    <>
      <OnboardingDialog
        dividers={false}
        unpaddingContent
        unpaddingFooter
        fullScreenMobile
        type="md"
        bgColor="#F9FAFB"
        title={`${l('payouts.create.title')} | ${l('payouts.create.review.menuTitle')}`}
        handleClose={() => history.push(Routes.PAYOUTS)}
        body={
          <div className="payout-create__layout">
            <Card>
              <div className="payout-create__card-content">
                <h2>{l('payouts.create.review.title')}</h2>

                <div className="payout-create__review-section">
                  <div className="payout-create__review-section__header">{l('payouts.create.review.paymentMethod')}</div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.review.availableBalance')}</span>
                    <span>
                      {balances.data?.availableBalance || 0} {balances.data?.currency || 'USD'}
                    </span>
                  </div>
                </div>

                <div className="payout-create__review-section">
                  <div className="payout-create__review-section__header with-action">
                    <span>{l('payouts.create.review.payoutData')}</span>
                    <button type="button" onClick={() => history.push(Routes.PAYOUTS_STEP_PAYOUT_DATA)}>
                      {l('common.edit')}
                    </button>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.field.amount')}</span>
                    <span>{createPayout.createPayoutData.transferAmount || 0}</span>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.field.currency')}</span>
                    <span>{createPayout.createPayoutData.currencyToPay || '-'}</span>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.drawer.exchangeRate')}</span>
                    <span>{createPayout.fee.data?.exchangeRate || '-'}</span>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.payoutData.totalFees')}</span>
                    <span>
                      {createPayout.fee.data?.feeAmount || 0} {createPayout.fee.data?.feeCurrency || 'USD'}
                    </span>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.field.purpose')}</span>
                    <span>
                      {createPayout.createPayoutData.purpose
                        ? payoutPurposeOptions[createPayout.createPayoutData.purpose]
                        : '-'}
                    </span>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.field.description')}</span>
                    <span>{createPayout.createPayoutData.description || '-'}</span>
                  </div>
                </div>

                <div className="payout-create__review-section">
                  <div className="payout-create__review-section__header with-action">
                    <span>{l('payouts.create.review.accountData')}</span>
                    <button type="button" onClick={() => history.push(Routes.PAYOUTS_STEP_ACCOUNT_DATA)}>
                      {l('common.edit')}
                    </button>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.field.country')}</span>
                    <span>{createPayout.createPayoutData.transferCountry || '-'}</span>
                  </div>
                  <div className="payout-create__review-section__row">
                    <span>{l('payouts.create.field.flowType')}</span>
                    <span>{getFlowTypeLabel(createPayout.createPayoutData.flowType)}</span>
                  </div>
                  {sortedBankAccountEntries.map(([key, value]) => (
                    <div key={key} className="payout-create__review-section__row">
                      <span>{getFieldLabel({ fieldPath: key })}</span>
                      <span>
                        {key === 'beneficiary.bank_account.code'
                          ? getBankDisplayName(value, createPayout.bankOptions)
                          : key === 'beneficiary.bank_account.type'
                            ? getAccountTypeDisplayLabel(String(value))
                            : String(value || '-')}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="payout-create__buttons payout-create__buttons--single">
                  <Button variant="contained" size="large" onClick={() => setShowOTPModal(true)}>
                    {l('payouts.create.review.confirmAndPay')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        }
        menuItems={getPayoutWizardMenuItems('review')}
      />
      <OTPModal open={showOTPModal} onClose={() => setShowOTPModal(false)} onSuccess={handleSuccess} />
    </>
  );
};

export default Review;
